import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Mail, MessageCircle } from "lucide-react";
import { APPLY } from "@/constants/testIds/clinic";
import { CLINIC, buildMailto, buildWhatsApp } from "@/constants/clinic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Multi-step Discovery Form
 * Step 0 — Objective (Reversal | Longevity)
 * Step 1A — Reversal focus (PCOS / Metabolic / Cardiac) + symptom duration
 * Step 1B — Longevity focus (Biomarkers / Cellular Energy / Cardio Fitness) + wearable
 * Step 2 — Format preference
 * Step 3 — Commitment check
 * Step 4 — Contact details + submit
 * Loading sequence (1.5s) then Success screen.
 */
const TOTAL_STEPS = 5;

const initialState = {
    objective: null, // "reversal" | "longevity"
    reversalFocus: [],
    reversalDuration: "",
    longevityFocus: [],
    wearable: "",
    format: null, // "in-clinic" | "hybrid" | "virtual"
    commitment: null, // "yes" | "explore"
    name: "",
    email: "",
    phone: "",
    city: "",
};

export default function DiscoveryForm() {
    const [data, setData] = useState(initialState);
    const [step, setStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const progress = useMemo(
        () => Math.min(100, ((step + 1) / TOTAL_STEPS) * 100),
        [step]
    );

    const update = (patch) => setData((d) => ({ ...d, ...patch }));
    const toggle = (key, value) =>
        setData((d) => ({
            ...d,
            [key]: d[key].includes(value)
                ? d[key].filter((v) => v !== value)
                : [...d[key], value],
        }));

    const validateStep = () => {
        const e = {};
        if (step === 0 && !data.objective) {
            e.objective =
                "Please choose a pathway so Dr. Ishani’s team can route your enquiry correctly.";
        }
        if (step === 1 && data.objective === "reversal") {
            if (data.reversalFocus.length === 0) {
                e.reversalFocus =
                    "Please select at least one focus area so we can prepare your clinical baseline.";
            }
            if (!data.reversalDuration.trim()) {
                e.reversalDuration =
                    "Please provide this detail so Dr. Ishani’s team can accurately assess your clinical baseline.";
            }
        }
        if (step === 1 && data.objective === "longevity") {
            if (data.longevityFocus.length === 0) {
                e.longevityFocus =
                    "Please select at least one focus area so we can design your protocol.";
            }
        }
        if (step === 2 && !data.format) {
            e.format = "Please choose the format that best fits your life right now.";
        }
        if (step === 3 && !data.commitment) {
            e.commitment =
                "Please indicate your commitment so we can match you with the right pathway.";
        }
        if (step === 4) {
            if (!data.name.trim()) e.name = "Please share your name so the team can address you properly.";
            if (!/^\S+@\S+\.\S+$/.test(data.email))
                e.email = "Please provide a reachable email so we can send your confirmation.";
            if (data.phone.replace(/\D/g, "").length < 8)
                e.phone =
                    "Please provide a number we can reach you on for the brief introductory call.";
            if (!data.city.trim())
                e.city = "Please tell us where you’re based so we can recommend a clinic format.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => {
        if (!validateStep()) return;
        setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
    };
    const back = () => setStep((s) => Math.max(0, s - 1));

    const buildSummaryText = () => {
        const lines = [
            `New consultation enquiry — Dr. Ishani Hanspal Clinic`,
            ``,
            `Name: ${data.name}`,
            `Email: ${data.email}`,
            `Phone: ${data.phone}`,
            `City: ${data.city}`,
            ``,
            `Pathway: ${data.objective === "reversal" ? "Disease Reversal" : "Longevity & Optimisation"}`,
        ];
        if (data.objective === "reversal") {
            lines.push(`Focus: ${data.reversalFocus.join(", ") || "—"}`);
            lines.push(`Symptom duration / context: ${data.reversalDuration || "—"}`);
        } else {
            lines.push(`Focus: ${data.longevityFocus.join(", ") || "—"}`);
            lines.push(`Wearable data: ${data.wearable || "—"}`);
        }
        lines.push(`Preferred format: ${data.format}`);
        lines.push(`Commitment: ${data.commitment === "yes" ? "Ready to begin" : "Exploring, want to learn more"}`);
        return lines.join("\n");
    };

    const persistApplication = async () => {
        // Silently persist to the clinic's records. Failure does NOT block the
        // success screen — the mailto/WhatsApp CTAs still reach the clinic.
        try {
            await axios.post(
                `${API}/applications`,
                {
                    objective: data.objective,
                    reversal_focus: data.reversalFocus,
                    reversal_duration: data.reversalDuration || null,
                    longevity_focus: data.longevityFocus,
                    wearable: data.wearable || null,
                    format: data.format,
                    commitment: data.commitment,
                    name: data.name.trim(),
                    email: data.email.trim(),
                    phone: data.phone.trim(),
                    city: data.city.trim(),
                },
                { timeout: 10000 }
            );
        } catch (err) {
            // Surfaced in browser console for debugging; user flow continues.
            console.error("[discovery-form] persistence failed", err);
        }
    };

    const submit = () => {
        if (!validateStep()) return;
        setLoading(true);
        setLoadingText("Encrypting Health Profile…");
        // Kick off persistence in parallel with the loading sequence
        persistApplication();
        setTimeout(() => setLoadingText("Structuring Clinical Baseline…"), 700);
        setTimeout(() => {
            setLoadingText("Application Received.");
            setLoading(false);
            setSuccess(true);
        }, 1500);
    };

    if (success) {
        const summary = buildSummaryText();
        return (
            <div
                data-testid={APPLY.successScreen}
                className="ihx-container py-24 lg:py-32"
            >
                <div className="max-w-2xl">
                    <div className="w-14 h-14 rounded-full bg-[hsl(var(--brand-ink-deep))] text-[hsl(var(--bg-chalk))] grid place-items-center mb-7">
                        <CheckCircle2 size={26} strokeWidth={1.4} />
                    </div>
                    <span className="ihx-eyebrow">Application received</span>
                    <h1 className="font-serif text-[38px] md:text-[52px] leading-[1.02] mt-5 tracking-[-0.02em]">
                        Thank you, {data.name.split(" ")[0] || "friend"}. Your clinical baseline is with <span className="ihx-italic">Dr. Ishani’s team.</span>
                    </h1>
                    <p className="ihx-lead mt-6">
                        To complete the loop, please send your enquiry directly to the clinic
                        using one of the channels below. Your responses are pre-attached so the
                        team can review them before reaching out within 24 working hours.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <a
                            data-testid={APPLY.successEmail}
                            href={buildMailto(
                                `Consultation enquiry — ${data.name}`,
                                summary
                            )}
                            className="ihx-btn-primary"
                        >
                            <Mail size={16} strokeWidth={1.6} /> Send securely via email
                        </a>
                        <a
                            data-testid={APPLY.successWhatsapp}
                            href={buildWhatsApp(summary)}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="ihx-btn-secondary"
                        >
                            <MessageCircle size={16} strokeWidth={1.6} /> Send via WhatsApp
                        </a>
                    </div>

                    <div className="mt-12 border-t border-[hsl(var(--border-stone))] pt-8 text-[13.5px] text-[hsl(var(--ink-muted))] leading-[1.7] max-w-xl">
                        Direct line: <a className="underline underline-offset-2" href={`tel:${CLINIC.phone.replace(/\s+/g, "")}`}>{CLINIC.phone}</a> · Email: <a className="underline underline-offset-2" href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="ihx-btn-link mt-10"
                    >
                        ← Return home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ihx-warm-surface ihx-grid-overlay" data-testid={APPLY.page}>
        <div className="ihx-container py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                {/* Sidebar */}
                <aside className="lg:col-span-4">
                    <span className="ihx-eyebrow">Discovery form</span>
                    <h1 className="font-serif text-[36px] md:text-[44px] leading-[1.05] mt-5 tracking-[-0.02em]">
                        A precise baseline, in about <span className="ihx-italic">ten minutes.</span>
                    </h1>
                    <p className="ihx-lead mt-6 max-w-md">
                        Your responses are reviewed personally by Dr. Hanspal’s team
                        before your first consultation. Nothing here is automated.
                    </p>

                    <div className="mt-9 flex items-center gap-3 text-[12.5px] text-[hsl(var(--ink-muted))]">
                        <Lock size={14} className="text-[hsl(var(--brand-gold))]" strokeWidth={1.6} />
                        End-to-end encrypted intake · Reviewed by clinical team
                    </div>

                    <div className="hidden lg:block mt-12 border-t border-[hsl(var(--border-stone))] pt-8 text-[13.5px] text-[hsl(var(--ink-muted))] leading-[1.7]">
                        Need a human first? <br />
                        <a className="underline underline-offset-4 text-[hsl(var(--brand-ink-deep))]" href={`mailto:${CLINIC.email}`}>
                            {CLINIC.email}
                        </a>
                        <br />
                        <a className="underline underline-offset-4 text-[hsl(var(--brand-ink-deep))]" href={`tel:${CLINIC.phone.replace(/\s+/g, "")}`}>
                            {CLINIC.phone}
                        </a>
                    </div>
                </aside>

                {/* Form */}
                <div className="lg:col-span-8">
                    {/* progress */}
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                            Step {step + 1} of {TOTAL_STEPS}
                        </span>
                        <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                            {Math.round(progress)}% complete
                        </span>
                    </div>
                    <div
                        data-testid={APPLY.progressBar}
                        className="h-[3px] w-full bg-[hsl(var(--border-stone))] rounded-full overflow-hidden"
                    >
                        <div
                            className="h-full bg-[hsl(var(--brand-gold))] transition-all duration-500 ease-out rounded-full"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    <div className="mt-10 min-h-[420px]">
                        {step === 0 && (
                            <StepObjective data={data} update={update} errors={errors} />
                        )}
                        {step === 1 && data.objective === "reversal" && (
                            <StepReversal data={data} update={update} toggle={toggle} errors={errors} />
                        )}
                        {step === 1 && data.objective === "longevity" && (
                            <StepLongevity data={data} update={update} toggle={toggle} errors={errors} />
                        )}
                        {step === 2 && <StepFormat data={data} update={update} errors={errors} />}
                        {step === 3 && <StepCommitment data={data} update={update} errors={errors} />}
                        {step === 4 && <StepContact data={data} update={update} errors={errors} />}
                    </div>

                    {/* Footer controls */}
                    <div className="mt-12 pt-6 border-t border-[hsl(var(--border-stone))] flex items-center justify-between gap-4">
                        <button
                            type="button"
                            data-testid={APPLY.backBtn}
                            onClick={back}
                            disabled={step === 0 || loading}
                            className="ihx-btn-link disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ArrowLeft size={15} strokeWidth={1.7} /> Back
                        </button>

                        {loading ? (
                            <div
                                data-testid={APPLY.loadingState}
                                className="flex items-center gap-3 text-[13px] tracking-[0.18em] uppercase text-[hsl(var(--brand-ink-deep))]"
                            >
                                <span className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--brand-gold))] animate-pulse" />
                                {loadingText}
                            </div>
                        ) : step < TOTAL_STEPS - 1 ? (
                            <button
                                type="button"
                                data-testid={APPLY.nextBtn}
                                onClick={next}
                                className="ihx-btn-primary"
                            >
                                Continue <span className="ihx-arrow" aria-hidden>→</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                data-testid={APPLY.submitBtn}
                                onClick={submit}
                                className="ihx-btn-primary"
                            >
                                Submit application <span className="ihx-arrow" aria-hidden>→</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}

/* ---------------- Step components ---------------- */
function FieldError({ children }) {
    if (!children) return null;
    return (
        <p className="text-[#991B1B] text-[13px] leading-[1.55] mt-3 max-w-md">
            {children}
        </p>
    );
}
function FieldsetHeader({ legend }) {
    return (
        <legend className="block">
            <span className="ihx-eyebrow">{legend}</span>
        </legend>
    );
}

function StepObjective({ data, update, errors }) {
    return (
        <fieldset>
            <FieldsetHeader legend="01 · Your Objective" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                Where would you like Dr. Hanspal’s team to start?
            </h2>
            <p className="text-[14.5px] text-slate-500 mt-3 max-w-lg">
                You can refine this later — but most patients arrive with one of these two intentions.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                    type="button"
                    data-testid="apply-objective-reversal"
                    onClick={() => update({ objective: "reversal" })}
                    data-selected={data.objective === "reversal"}
                    className="ihx-chip flex-col items-start gap-3 p-6 text-left"
                >
                    <div className="flex items-center gap-3 w-full">
                        <span className="ihx-chip-dot" />
                        <span className="text-[12px] tracking-[0.22em] uppercase opacity-70">Pathway A</span>
                    </div>
                    <span className="font-serif text-[22px] leading-tight">
                        Reverse a diagnosis
                    </span>
                    <span className="text-[13.5px] opacity-80 leading-[1.6]">
                        PCOS, prediabetes, type 2 diabetes, fatty liver, hypertension, cardiac rehabilitation.
                    </span>
                </button>
                <button
                    type="button"
                    data-testid="apply-objective-longevity"
                    onClick={() => update({ objective: "longevity" })}
                    data-selected={data.objective === "longevity"}
                    className="ihx-chip flex-col items-start gap-3 p-6 text-left"
                >
                    <div className="flex items-center gap-3 w-full">
                        <span className="ihx-chip-dot" />
                        <span className="text-[12px] tracking-[0.22em] uppercase opacity-70">Pathway B</span>
                    </div>
                    <span className="font-serif text-[22px] leading-tight">
                        Engineer longevity
                    </span>
                    <span className="text-[13.5px] opacity-80 leading-[1.6]">
                        Biomarker optimisation, cellular energy, cardiorespiratory fitness, the next 40 years.
                    </span>
                </button>
            </div>
            <FieldError>{errors.objective}</FieldError>
        </fieldset>
    );
}

function StepReversal({ data, toggle, update, errors }) {
    const opts = [
        { id: "PCOS", testId: "apply-reversal-focus-pcos", t: "PCOS & Hormonal Health", d: "Cycle, androgen, insulin." },
        { id: "Metabolic Health", testId: "apply-reversal-focus-metabolic", t: "Metabolic Health", d: "T2D, prediabetes, fatty liver." },
        { id: "Cardiac Rehabilitation", testId: "apply-reversal-focus-cardiac", t: "Cardiac Rehabilitation", d: "Lipids, BP, post-event recovery." },
    ];
    return (
        <fieldset>
            <FieldsetHeader legend="02 · Reversal Focus" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                Which condition would you like to focus on first?
            </h2>
            <p className="text-[14.5px] text-slate-500 mt-3 max-w-lg">
                Select all that apply. The clinical team will sequence them with you.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                {opts.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        data-testid={o.testId}
                        onClick={() => toggle("reversalFocus", o.id)}
                        data-selected={data.reversalFocus.includes(o.id)}
                        className="ihx-chip flex-col items-start gap-2 p-5 text-left"
                    >
                        <div className="flex items-center gap-3 w-full">
                            <span className="ihx-chip-dot" />
                        </div>
                        <span className="font-serif text-[18px] leading-tight">{o.t}</span>
                        <span className="text-[12.5px] opacity-80 leading-snug">{o.d}</span>
                    </button>
                ))}
            </div>
            <FieldError>{errors.reversalFocus}</FieldError>

            <div className="mt-10">
                <label
                    htmlFor="symptom-duration"
                    className="block text-[12.5px] tracking-[0.22em] uppercase text-slate-500 mb-3"
                >
                    Symptom duration / brief context
                </label>
                <textarea
                    id="symptom-duration"
                    data-testid="apply-reversal-symptom-duration"
                    rows={3}
                    placeholder="e.g. PCOS diagnosed 2019, irregular cycles for ~3 years, fasting insulin 18…"
                    value={data.reversalDuration}
                    onChange={(e) => update({ reversalDuration: e.target.value })}
                    className={`ihx-field ${errors.reversalDuration ? "ihx-field--error" : ""}`}
                />
                <FieldError>{errors.reversalDuration}</FieldError>

                {/* Lab upload — UX touch only */}
                <div
                    data-testid="apply-upload-hint"
                    className="mt-6 flex items-start gap-3 p-4 border border-dashed border-slate-300 bg-[#F8FAFC]"
                >
                    <Lock size={16} className="text-[#B45309] mt-[2px]" strokeWidth={1.6} />
                    <div className="text-[13px] leading-[1.6] text-slate-600">
                        Have past labs? You’ll be invited to securely attach them after our team
                        replies — <span className="italic">records encrypted and attached to your profile</span>.
                    </div>
                </div>
            </div>
        </fieldset>
    );
}

function StepLongevity({ data, toggle, update, errors }) {
    const opts = [
        { id: "Biomarkers", testId: "apply-longevity-focus-biomarkers", t: "Biomarker Panels", d: "ApoB, hsCRP, hormones, micronutrients." },
        { id: "Cellular Energy", testId: "apply-longevity-focus-cellular", t: "Cellular Energy", d: "Mitochondrial, sleep, circadian." },
        { id: "Cardio Fitness", testId: "apply-longevity-focus-cardio", t: "Cardiorespiratory Fitness", d: "VO₂max, zone-2, strength." },
    ];
    return (
        <fieldset>
            <FieldsetHeader legend="02 · Longevity Focus" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                Which dimension matters most to you right now?
            </h2>
            <p className="text-[14.5px] text-slate-500 mt-3 max-w-lg">
                Most patients pick more than one — the protocol will prioritise.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                {opts.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        data-testid={o.testId}
                        onClick={() => toggle("longevityFocus", o.id)}
                        data-selected={data.longevityFocus.includes(o.id)}
                        className="ihx-chip flex-col items-start gap-2 p-5 text-left"
                    >
                        <div className="flex items-center gap-3 w-full">
                            <span className="ihx-chip-dot" />
                        </div>
                        <span className="font-serif text-[18px] leading-tight">{o.t}</span>
                        <span className="text-[12.5px] opacity-80 leading-snug">{o.d}</span>
                    </button>
                ))}
            </div>
            <FieldError>{errors.longevityFocus}</FieldError>

            <div className="mt-10">
                <label
                    htmlFor="wearable"
                    className="block text-[12.5px] tracking-[0.22em] uppercase text-slate-500 mb-3"
                >
                    Wearable / continuous data you already track (optional)
                </label>
                <input
                    id="wearable"
                    data-testid="apply-longevity-wearable"
                    placeholder="e.g. Oura, Whoop, Apple Watch, CGM (Libre/Dexcom), Garmin…"
                    value={data.wearable}
                    onChange={(e) => update({ wearable: e.target.value })}
                    className="ihx-field"
                />
            </div>
        </fieldset>
    );
}

function StepFormat({ data, update, errors }) {
    const opts = [
        { id: "in-clinic", testId: "apply-format-in-clinic", t: "In-Clinic", d: "Chennai or Gurugram, high-touch baseline." },
        { id: "hybrid", testId: "apply-format-hybrid", t: "Hybrid", d: "In-clinic baseline, virtual continuity." },
        { id: "virtual", testId: "apply-format-virtual", t: "Virtual", d: "Fully remote, anywhere in the world." },
    ];
    return (
        <fieldset>
            <FieldsetHeader legend="03 · Format Preference" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                How would you like to receive your care?
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                {opts.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        data-testid={o.testId}
                        onClick={() => update({ format: o.id })}
                        data-selected={data.format === o.id}
                        className="ihx-chip flex-col items-start gap-2 p-5 text-left"
                    >
                        <div className="flex items-center gap-3 w-full">
                            <span className="ihx-chip-dot" />
                        </div>
                        <span className="font-serif text-[19px] leading-tight">{o.t}</span>
                        <span className="text-[12.5px] opacity-80 leading-snug">{o.d}</span>
                    </button>
                ))}
            </div>
            <FieldError>{errors.format}</FieldError>
        </fieldset>
    );
}

function StepCommitment({ data, update, errors }) {
    const opts = [
        { id: "yes", testId: "apply-commitment-yes", t: "I’m ready to begin", d: "I’m prepared to commit to a 12-week protocol." },
        { id: "explore", testId: "apply-commitment-explore", t: "I want to learn more first", d: "I’d like a discovery call before deciding." },
    ];
    return (
        <fieldset>
            <FieldsetHeader legend="04 · Commitment Check" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                Where are you in your decision?
            </h2>
            <p className="text-[14.5px] text-slate-500 mt-3 max-w-lg">
                There’s no wrong answer. We just want to set up your first interaction
                in a way that respects your time.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {opts.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        data-testid={o.testId}
                        onClick={() => update({ commitment: o.id })}
                        data-selected={data.commitment === o.id}
                        className="ihx-chip flex-col items-start gap-2 p-6 text-left"
                    >
                        <div className="flex items-center gap-3 w-full">
                            <span className="ihx-chip-dot" />
                        </div>
                        <span className="font-serif text-[20px] leading-tight">{o.t}</span>
                        <span className="text-[13px] opacity-80 leading-snug">{o.d}</span>
                    </button>
                ))}
            </div>
            <FieldError>{errors.commitment}</FieldError>
        </fieldset>
    );
}

function StepContact({ data, update, errors }) {
    return (
        <fieldset>
            <FieldsetHeader legend="05 · Reaching You" />
            <h2 className="font-serif text-[28px] md:text-[34px] leading-[1.12] mt-4 max-w-xl">
                Last step — how should the team get back to you?
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                    id="name"
                    label="Full name"
                    placeholder="e.g. Priya Menon"
                    value={data.name}
                    onChange={(v) => update({ name: v })}
                    error={errors.name}
                    testId="apply-contact-name"
                />
                <Field
                    id="email"
                    label="Email"
                    type="email"
                    placeholder="you@domain.com"
                    value={data.email}
                    onChange={(v) => update({ email: v })}
                    error={errors.email}
                    testId="apply-contact-email"
                />
                <Field
                    id="phone"
                    label="Phone / WhatsApp"
                    placeholder="+91 98 …"
                    value={data.phone}
                    onChange={(v) => update({ phone: v })}
                    error={errors.phone}
                    testId="apply-contact-phone"
                />
                <Field
                    id="city"
                    label="City of residence"
                    placeholder="e.g. Bengaluru"
                    value={data.city}
                    onChange={(v) => update({ city: v })}
                    error={errors.city}
                    testId="apply-contact-city"
                />
            </div>

            <p className="mt-8 text-[12.5px] text-slate-500 leading-[1.7] max-w-xl">
                By submitting, you consent to Dr. Hanspal’s team contacting you regarding this
                enquiry. Your information is not shared with third parties.
            </p>
        </fieldset>
    );
}

function Field({ id, label, type = "text", value, onChange, placeholder, error, testId }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="block text-[12.5px] tracking-[0.22em] uppercase text-slate-500 mb-3"
            >
                {label}
            </label>
            <input
                id={id}
                data-testid={testId}
                type={type}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className={`ihx-field ${error ? "ihx-field--error" : ""}`}
                aria-invalid={!!error}
            />
            <FieldError>{error}</FieldError>
        </div>
    );
}
